/**
 * Intent engine: turns a finger stroke into the shape the user meant, using
 * the plan's vector network. Runs every frame while the finger is down.
 *
 *   trace   – the stroke follows plan line work → the feature's real centreline
 *             (through junctions and across dashes), with the band sized to a
 *             detected parallel pair (duct banks, trenches, roads)
 *   region  – a closed loop or a scribble → the enclosed face(s) of the plan
 *   free    – nothing to lock on to → the stroke itself, cleaned and smoothed
 */
import { chaikin, dedupe, dist, nearestOnSegment, pointAlong, polyBounds, polylineLength, segmentsIntersect, simplify, type Rect } from '../editor/geom.ts';
import { RailIndex } from './rails.ts';
import type { Form, Vec } from '../../shared/doc.ts';
import type { SketchReading } from '../editor/sketch.ts';
import { expandPolygon, faceContour, floodFace, type Face } from './faces.ts';
import { labelComponents } from './raster.ts';
import type { Seg, VectorPlan } from './types.ts';
import type { Luma } from './ink.ts';

export type Intent =
  | { kind: 'band'; pts: number[]; polys?: number[][]; path: number[]; width: number; label: string; version?: number; thinking?: boolean } // pts: closed border polygon; polys: one per stretch while live
  | { kind: 'trace'; pts: number[]; width: number; label: string }
  | { kind: 'region'; pts: number[]; label: string }
  | { kind: 'free-stroke'; pts: number[]; width: number; label: string }
  | { kind: 'free-poly'; pts: number[]; label: string }
  | { kind: 'shape'; form: Form; reading: SketchReading; label: string } // a sketched loop made perfect (editor/sketch.ts)
  | { kind: 'none' };

interface Hit {
  seg: Seg;
  point: Vec;
  dist: number;
  segIndex: number; // vertex index of the segment's sub-segment
  t: number;
}

const CELL = 48;

export class IntentEngine {
  readonly plan: VectorPlan;
  #rails: RailIndex | null = null;
  #grid = new Map<number, number[]>(); // cell key -> seg ids
  #cols: number;
  #faceCache = new Map<string, Face | null>();

  constructor(plan: VectorPlan) {
    this.plan = plan;
    this.#cols = Math.ceil(plan.w / CELL) + 1;
    this.#index();
  }

  /** The segments by grid cell (a leader's are left out: nothing asks for them). */
  #index(): void {
    const plan = this.plan;
    this.#grid.clear();
    for (const s of plan.segs) {
      if (s.leader) continue;
      const x0 = Math.floor(s.minX / CELL),
        x1 = Math.floor(s.maxX / CELL),
        y0 = Math.floor(s.minY / CELL),
        y1 = Math.floor(s.maxY / CELL);
      for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) {
        const key = cy * this.#cols + cx;
        const arr = this.#grid.get(key);
        if (arr) arr.push(s.id);
        else this.#grid.set(key, [s.id]);
      }
    }
  }

  /**
   * The plan's leaders (drawing.ts: each a label's line to what it labels): the segments lying
   * along one — and those of its arrowhead or dot, at its tip — are no line work, and are never
   * snapped to or followed from now on.
   */
  setLeaders(leaders: readonly { pts: number[] }[]): void {
    if (!leaders.length) return;
    // (the leg of the leader a point lies along, within tol: -1 for none)
    const legNear = (x: number, y: number, L: { pts: number[] }, tol: number): number => {
      const p = L.pts;
      for (let i = 0; i + 3 < p.length; i += 2) {
        const ax = p[i]!,
          ay = p[i + 1]!,
          ex = p[i + 2]! - ax,
          ey = p[i + 3]! - ay;
        const l2 = ex * ex + ey * ey || 1e-9;
        const t = Math.max(0, Math.min(1, ((x - ax) * ex + (y - ay) * ey) / l2));
        if (Math.hypot(x - ax - ex * t, y - ay - ey * t) <= tol) return i;
      }
      return -1;
    };
    const boxes = leaders.map((L) => {
      let x0 = Infinity,
        y0 = Infinity,
        x1 = -Infinity,
        y1 = -Infinity;
      for (let i = 0; i < L.pts.length; i += 2) {
        x0 = Math.min(x0, L.pts[i]!);
        x1 = Math.max(x1, L.pts[i]!);
        y0 = Math.min(y0, L.pts[i + 1]!);
        y1 = Math.max(y1, L.pts[i + 1]!);
      }
      return { x0, y0, x1, y1 };
    });
    let marked = 0;
    for (const s of this.plan.segs) {
      if (s.leader) continue;
      // (a piece a few pixels long is the knot where a leader crosses a line: the line's too)
      const p = s.pts;
      const cx = p[p.length - 2]! - p[0]!,
        cy = p[p.length - 1]! - p[1]!;
      const chord = Math.hypot(cx, cy);
      if (chord < 4) continue;
      const tol = Math.max(2, s.thick + 1);
      for (let j = 0; j < leaders.length; j++) {
        const b = boxes[j]!,
          L = leaders[j]!;
        if (s.maxX < b.x0 - tol || s.minX > b.x1 + tol || s.maxY < b.y0 - tol || s.minY > b.y1 + tol) continue;
        // along it all but a pixel or two, and running its way
        let n = 0,
          on = 0,
          leg = -1;
        const visit = (x: number, y: number) => {
          n++;
          const g = legNear(x, y, L, tol);
          if (g >= 0) {
            on++;
            leg = g;
          }
        };
        for (let i = 0; i + 3 < p.length; i += 2) {
          const l = Math.hypot(p[i + 2]! - p[i]!, p[i + 3]! - p[i + 1]!);
          const k = Math.max(1, Math.ceil(l / 1.5));
          for (let q = 0; q < k; q++) visit(p[i]! + ((p[i + 2]! - p[i]!) * q) / k, p[i + 1]! + ((p[i + 3]! - p[i + 1]!) * q) / k);
        }
        visit(p[p.length - 2]!, p[p.length - 1]!);
        if (on < 0.9 * n || leg < 0) continue;
        const lx = L.pts[leg + 2]! - L.pts[leg]!,
          ly = L.pts[leg + 3]! - L.pts[leg + 1]!;
        if (Math.abs(cx * lx + cy * ly) / (chord * (Math.hypot(lx, ly) || 1)) >= Math.cos((20 * Math.PI) / 180)) {
          s.leader = true;
          marked++;
          break;
        }
      }
    }
    if (!marked) return;
    this.#index();
    this.#rails = null;
    this.#faceCache.clear();
  }

  /** The plan's ink at full resolution (null for very large plans or synthetic test plans without it). */
  get luma(): Luma | null {
    return (this.#luma ??= this.plan.luma ? { w: this.plan.w, h: this.plan.h, data: this.plan.luma } : null);
  }
  #luma: Luma | null | undefined;
  /** The plan's lines as long rails (grown on demand, cached for the plan's lifetime). */
  get rails(): RailIndex {
    return (this.#rails ??= new RailIndex(this.plan, (p, r) => this.#segsNear(p, r)));
  }

  /** Ids of segments whose bounds come within `radius` of p. */
  segsNear(p: Vec, radius: number): number[] {
    return this.#segsNear(p, radius);
  }

  /** Drop per-gesture face floods (called when a stroke ends). */
  clearCache(): void {
    this.#faceCache.clear();
  }

  /* ---------------------------------------------------------- queries */

  #segsNear(p: Vec, radius: number): number[] {
    const out: number[] = [];
    const seen = new Set<number>();
    const x0 = Math.floor((p.x - radius) / CELL),
      x1 = Math.floor((p.x + radius) / CELL),
      y0 = Math.floor((p.y - radius) / CELL),
      y1 = Math.floor((p.y + radius) / CELL);
    for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) {
      const arr = this.#grid.get(cy * this.#cols + cx);
      if (!arr) continue;
      for (const id of arr) if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
    return out;
  }

  /** Nearest point on any segment within maxDist of p. */
  nearest(p: Vec, maxDist: number, exclude?: (s: Seg) => boolean): Hit | null {
    let best: Hit | null = null;
    for (const id of this.#segsNear(p, maxDist)) {
      const s = this.plan.segs[id]!;
      if (exclude && exclude(s)) continue;
      if (p.x < s.minX - maxDist || p.x > s.maxX + maxDist || p.y < s.minY - maxDist || p.y > s.maxY + maxDist) continue;
      const pts = s.pts;
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const r = nearestOnSegment(p, { x: pts[i]!, y: pts[i + 1]! }, { x: pts[i + 2]!, y: pts[i + 3]! });
        if (r.d < maxDist && (!best || r.d < best.dist)) best = { seg: s, point: r.p, dist: r.d, segIndex: i / 2, t: r.t };
      }
    }
    return best;
  }

  /** Nearest junction/endpoint node within maxDist. */
  nearestNode(p: Vec, maxDist: number): { x: number; y: number; id: number } | null {
    let best: { x: number; y: number; id: number } | null = null;
    let bd = maxDist;
    for (const id of this.#segsNear(p, maxDist)) {
      const s = this.plan.segs[id]!;
      for (const nid of [s.a, s.b]) {
        const n = this.plan.nodes[nid]!;
        const d = Math.hypot(n.x - p.x, n.y - p.y);
        if (d < bd) {
          bd = d;
          best = { x: n.x, y: n.y, id: nid };
        }
      }
    }
    return best;
  }

  /**
   * The enclosed area of the plan a point stands in — its outline in image px, on its bounding
   * lines — or null when the point is on ink, the area is open to the plan's edge, or it is
   * larger than `maxArea` (image px²). Small gaps in its outline (a dash, a symbol) are closed.
   */
  faceAt(p: Vec, maxArea: number): number[] | null {
    const { k, cw, ch } = this.plan;
    const sx = Math.floor(p.x * k),
      sy = Math.floor(p.y * k);
    if (sx < 0 || sy < 0 || sx >= cw || sy >= ch) return null;
    const cap = Math.min(0.35 * cw * ch, maxArea * k * k);
    for (const r of [1, 2]) {
      const f = floodFace(this.plan, sx, sy, r, cap);
      if (f) return faceContour(f, this.plan, 1.2);
    }
    return null;
  }

  /** Magnetic snap for vertex editing: junctions first, then lines. */
  snapPoint(p: Vec, tol: number): { p: Vec; kind: 'node' | 'line' | null } {
    const n = this.nearestNode(p, tol * 0.9);
    if (n) return { p: { x: n.x, y: n.y }, kind: 'node' };
    const h = this.nearest(p, tol * 0.7);
    if (h) return { p: h.point, kind: 'line' };
    return { p, kind: null };
  }

  /* ---------------------------------------------------------- analysis */

  /**
   * @param stroke  finger path in image px (flat)
   * @param tol     snapping tolerance in image px (≈ 12 screen px / zoom)
   * @param defaultWidth band width when no parallel pair is found (image px)
   */
  analyze(stroke: number[], tol: number, defaultWidth: number): Intent {
    const pts = dedupe(stroke, 1);
    const n = pts.length / 2;
    if (n < 2) return { kind: 'none' };
    const L = polylineLength(pts);
    if (L < 4 * tol) return { kind: 'none' };
    const bbox = polyBounds(pts);
    const start = { x: pts[0]!, y: pts[1]! };
    const end = { x: pts[pts.length - 2]!, y: pts[pts.length - 1]! };
    const closure = dist(start, end);
    const diag = Math.hypot(bbox.w, bbox.h);
    const loopArea = Math.abs(polygonArea(pts));
    // A loop encloses real area for its length (circle: 0.08·L², square: 0.0625·L²);
    // a back-and-forth scribble that happens to end near its start does not.
    const { closed, scribble } = strokeForm(pts, tol);
    void closure;
    void diag;
    void loopArea;
    if (closed || scribble) {
      const region = this.#region(pts, bbox, closed, tol);
      if (region) return region;
      if (closed) {
        const loop = this.#snapPolygon(cleanLoop(pts, tol), tol);
        return { kind: 'free-poly', pts: asRectangle(loop, tol) ?? loop, label: 'Area' };
      }
      // a scribble with nothing to fill: treat as a free area around the scribble
      return { kind: 'free-poly', pts: cleanLoop(hull(pts), tol), label: 'Area' };
    }
    const traced = this.#trace(pts, tol, defaultWidth);
    if (traced) return traced;
    // Finger running between two parallel lines (a trench or duct bank without a centreline).
    const clean = cleanStroke(pts, tol);
    const fit = this.#fitBand(clean, tol, defaultWidth);
    if (fit.label === 'Between lines') return { kind: 'trace', pts: simplify(fit.midline, tol * 0.12), width: fit.width, label: fit.label };
    return { kind: 'free-stroke', pts: clean, width: defaultWidth, label: 'Free line' };
  }

  /**
   * Band for a finished traced path: default width on a solid line, the span
   * of the parallel pair when the path is a dashed centreline or runs between
   * lines. Public entry used by the live tracer on release.
   */
  bandFor(path: number[], tol: number, defaultWidth: number, fingerOffset = 0, pixelPath: number[] = path): { pts: number[]; width: number; label: string } {
    void pixelPath;
    const { k } = this.plan;
    const cap = 0.032 * Math.max(this.plan.w, this.plan.h); // widest plausible corridor (image px)
    const around = this.#linesAround(path, cap * k);
    const thick = around.thick / k;
    // Signed offsets (image px) of every consistent parallel line; 0 is the traced line itself.
    const offsets = [0, ...around.left.map((d) => d / k), ...around.right.map((d) => -d / k)].sort((a, b) => a - b);
    const f = fingerOffset;
    const slack = 0.3 * tol;
    let best: { a: number; b: number } | null = null;
    for (let i = 0; i < offsets.length; i++) for (let j = i + 1; j < offsets.length; j++) {
      const a = offsets[i]!,
        b = offsets[j]!;
      const width = b - a;
      if (width > cap || width < 3) continue;
      if (f < a - slack || f > b + slack) continue; // the finger must run inside the pair
      // When the traced line is itself an edge of the pair, the finger must clearly be inside.
      if (a === 0 && f < 0.35 * tol) continue;
      if (b === 0 && f > -0.35 * tol) continue;
      if (!best || width > best.b - best.a || (width === best.b - best.a && Math.abs((a + b) / 2 - f) < Math.abs((best.a + best.b) / 2 - f))) best = { a, b };
    }
    this.lastDebug = { ...(this.lastDebug as object), fingerOffset: +f.toFixed(1), offsets: offsets.map((o) => +o.toFixed(1)), cap: +cap.toFixed(0), pair: best };
    if (!best) return { pts: path, width: defaultWidth, label: 'Along line' };
    const margin = Math.max(2, thick);
    // Re-centre exactly between the two bracketing lines, sample by sample, so finger
    // jitter never shows in the band.
    const centred = this.#recentre(path, best.a, best.b, cap * k);
    return { pts: simplify(centred, tol * 0.12), width: best.b - best.a + 2 * margin + thick, label: 'Between lines' };
  }

  /** Midline between the lines found at signed offsets ≈ a and b (image px) from the path. */
  #recentre(path: number[], a: number, b: number, maxW: number): number[] {
    const { k } = this.plan;
    const samples = resample(path, 8);
    const out: number[] = [];
    for (let i = 0; i < samples.length; i += 2) {
      const x = samples[i]!,
        y = samples[i + 1]!;
      const t = tangentAt(path, x, y);
      const nx = -t.y,
        ny = t.x;
      const findAt = (target: number): number | null => {
        if (Math.abs(target) < 1) return 0;
        const sgn = Math.sign(target);
        const hits = this.#walkAll(x * k, y * k, nx * sgn, ny * sgn, 1, maxW).map((d) => (d / k) * sgn);
        let best: number | null = null;
        for (const h of hits) if (Math.abs(h - target) <= 4 && (best === null || Math.abs(h - target) < Math.abs(best - target))) best = h;
        return best;
      };
      const oa = findAt(a) ?? a,
        ob = findAt(b) ?? b;
      const c = (oa + ob) / 2;
      out.push(x + nx * c, y + ny * c);
    }
    return out;
  }

  /** Consistent parallel lines on each side of a path (working px), plus the path's own thickness. */
  #linesAround(path: number[], maxW: number): { left: number[]; right: number[]; thick: number } {
    const { k } = this.plan;
    const samples = resample(path, Math.max(6, 8));
    const nS = samples.length / 2;
    const leftHits: number[][] = [],
      rightHits: number[][] = [];
    let ownThick = 0;
    for (let i = 0; i < samples.length; i += 2) {
      const x = samples[i]!,
        y = samples[i + 1]!;
      const t = tangentAt(path, x, y);
      const nx = -t.y,
        ny = t.x;
      const own = this.#walk(x * k, y * k, nx, ny, 0, 6, true) + this.#walk(x * k, y * k, -nx, -ny, 0, 6, true);
      ownThick += own;
      leftHits.push(this.#walkAll(x * k, y * k, nx, ny, own / 2 + 2, maxW));
      rightHits.push(this.#walkAll(x * k, y * k, -nx, -ny, own / 2 + 2, maxW));
    }
    const hist = (hits: number[][]) => {
      const c = new Map<number, number>();
      for (const h of hits) for (const d of h) c.set(Math.round(d / 4) * 4, (c.get(Math.round(d / 4) * 4) ?? 0) + 1);
      return [...c.entries()].sort((a, b) => a[0] - b[0]).map(([d, n]) => `${d}:${(n / nS).toFixed(2)}`).join(' ');
    };
    this.lastDebug = { nS, leftHist: hist(leftHits), rightHist: hist(rightHits) };
    return { left: consistentDistances(leftHits, nS, 0.9), right: consistentDistances(rightHits, nS, 0.9), thick: nS ? ownThick / nS : 2 };
  }

  /* ------------------------------------------------------------- trace */

  #trace(pts: number[], tol: number, defaultWidth: number): Intent | null {
    const n = pts.length / 2;
    // How much of the stroke lies on line work?
    let on = 0;
    const hits: (Hit | null)[] = [];
    for (let i = 0; i < n; i++) {
      const h = this.nearest({ x: pts[2 * i]!, y: pts[2 * i + 1]! }, tol);
      hits.push(h);
      if (h) on++;
    }
    if (on / n < 0.5) return null;
    const path = this.#corridorPath(pts, hits, tol);
    if (!path) return null;
    const L = polylineLength(pts);
    const pl = polylineLength(path);
    if (pl > 1.7 * L + 4 * tol || pl < 0.45 * L) return null;
    const smooth = smoothPath(path, tol);
    // A continuous solid line is the feature itself; a dashed centreline (or a finger running
    // between lines) marks a corridor whose width comes from the parallel line work.
    const coverage = this.#inkCoverage(smooth);
    this.lastDebug = { coverage: +coverage.toFixed(3) };
    if (coverage >= 0.93) return { kind: 'trace', pts: simplify(smooth, tol * 0.12), width: defaultWidth, label: 'Along line' };
    const { midline, width, label } = this.#fitBand(smooth, tol, defaultWidth);
    return { kind: 'trace', pts: simplify(midline, tol * 0.12), width, label };
  }

  /** Fraction of points along a path (resampled) that sit on line ink (±1 px). */
  #inkCoverage(path: number[]): number {
    const { lineMask, cw, ch, k } = this.plan;
    const rs = resample(path, 4);
    let on = 0,
      n = 0;
    for (let i = 0; i < rs.length; i += 2) {
      const x = Math.floor(rs[i]! * k),
        y = Math.floor(rs[i + 1]! * k);
      n++;
      let hit = false;
      for (let dy = -1; dy <= 1 && !hit; dy++) for (let dx = -1; dx <= 1; dx++) {
        const xx = x + dx,
          yy = y + dy;
        if (xx >= 0 && yy >= 0 && xx < cw && yy < ch && lineMask[yy * cw + xx]) {
          hit = true;
          break;
        }
      }
      if (hit) on++;
    }
    return n ? on / n : 0;
  }

  /**
   * Shortest path through the line network between the projections of the
   * stroke's ends, restricted to segments near the stroke, with costs that
   * favour staying close to the finger.
   */
  #corridorPath(stroke: number[], hits: (Hit | null)[], tol: number): number[] | null {
    const plan = this.plan;
    const corridor = 3 * tol;
    const n = hits.length;
    // Start/end candidates: every hit within the first/last 12% of the stroke.
    const head = Math.max(1, Math.floor(n * 0.12));
    const starts = new Map<number, Hit>();
    const ends = new Map<number, Hit>();
    for (let i = 0; i < head; i++) {
      const h = hits[i];
      if (h && (!starts.has(h.seg.id) || h.dist < starts.get(h.seg.id)!.dist)) starts.set(h.seg.id, h);
    }
    for (let i = n - 1; i >= n - head; i--) {
      const h = hits[i];
      if (h && (!ends.has(h.seg.id) || h.dist < ends.get(h.seg.id)!.dist)) ends.set(h.seg.id, h);
    }
    if (!starts.size || !ends.size) return null;
    // Candidate segments: within the corridor of the stroke polyline.
    const cand = new Map<number, number>(); // seg id -> mean distance to stroke
    const b = polyBounds(stroke);
    const probe = { x: 0, y: 0 };
    for (const id of this.#segsInRect({ x: b.x - corridor, y: b.y - corridor, w: b.w + 2 * corridor, h: b.h + 2 * corridor })) {
      const s = plan.segs[id]!;
      let sum = 0,
        cnt = 0,
        ok = true;
      for (let i = 0; i < s.pts.length; i += 2) {
        probe.x = s.pts[i]!;
        probe.y = s.pts[i + 1]!;
        const d = distToPolyline(probe, stroke);
        if (d > corridor * 1.5) {
          ok = false;
          break;
        }
        sum += d;
        cnt++;
      }
      if (ok && cnt) cand.set(id, sum / cnt);
    }
    for (const id of [...starts.keys(), ...ends.keys()]) if (!cand.has(id)) cand.set(id, tol);
    if (cand.size === 0) return null;
    // Graph: plan nodes + virtual nodes at each start/end projection + super source/sink.
    type Edge = { to: number; cost: number; geom: number[] };
    const adj = new Map<number, Edge[]>();
    const add = (a: number, b: number, cost: number, geom: number[]) => {
      (adj.get(a) ?? adj.set(a, []).get(a)!).push({ to: b, cost, geom });
      (adj.get(b) ?? adj.set(b, []).get(b)!).push({ to: a, cost, geom: reversePts(geom) });
    };
    const SRC = -1,
      SNK = -2;
    let virt = -10;
    for (const [id, meanD] of cand) {
      const s = plan.segs[id]!;
      // Segments that run across the finger's direction (connectors between parallel lines)
      // are expensive, so the path does not hop between parallels.
      const chordX = s.pts[s.pts.length - 2]! - s.pts[0]!,
        chordY = s.pts[s.pts.length - 1]! - s.pts[1]!;
      const cl = Math.hypot(chordX, chordY);
      let dirPenalty = 1;
      if (cl > tol * 0.5) {
        const mid = { x: (s.pts[0]! + s.pts[s.pts.length - 2]!) / 2, y: (s.pts[1]! + s.pts[s.pts.length - 1]!) / 2 };
        const t = tangentAt(stroke, mid.x, mid.y);
        const align = Math.abs((chordX * t.x + chordY * t.y) / cl);
        dirPenalty = 1 + 4 * (1 - align);
      }
      const w = (1 + (4 * meanD) / tol) * dirPenalty;
      const cuts: { t: number; node: number; hit: Hit; kind: 'start' | 'end' }[] = [];
      const st = starts.get(id);
      if (st) cuts.push({ t: st.segIndex + st.t, node: virt--, hit: st, kind: 'start' });
      const en = ends.get(id);
      if (en) cuts.push({ t: en.segIndex + en.t, node: virt--, hit: en, kind: 'end' });
      if (!cuts.length) {
        add(s.a, s.b, s.len * w, s.pts);
        continue;
      }
      cuts.sort((p, q) => p.t - q.t);
      let prevNode = s.a,
        prevT = 0;
      for (const c of cuts) {
        const g = slicePolyline(s.pts, prevT, c.t);
        add(prevNode, c.node, polylineLength(g) * w + 1e-3, g);
        // connect to source/sink with the finger distance as cost
        if (c.kind === 'start') add(SRC, c.node, c.hit.dist, [c.hit.point.x, c.hit.point.y]);
        else add(c.node, SNK, c.hit.dist, [c.hit.point.x, c.hit.point.y]);
        prevNode = c.node;
        prevT = c.t;
      }
      const g = slicePolyline(s.pts, prevT, s.pts.length / 2 - 1);
      add(prevNode, s.b, polylineLength(g) * w + 1e-3, g);
    }
    for (const br of plan.bridges) {
      if (!adj.has(br.a) || !adj.has(br.b)) continue;
      const na = plan.nodes[br.a]!,
        nb = plan.nodes[br.b]!;
      add(br.a, br.b, br.len * 1.5, [na.x, na.y, nb.x, nb.y]);
    }
    // The finger bridges gaps the plan does not: link line ends that lie along the stroke.
    // Only true continuations qualify: both line ends must point at each other across the gap.
    const strokeLen = polylineLength(stroke);
    const maxJump = Math.min(0.3 * strokeLen, 8 * tol);
    const endDir = new Map<number, Vec>();
    for (const id of cand.keys()) {
      const sg = plan.segs[id]!;
      for (const nid of [sg.a, sg.b]) {
        const nd = plan.nodes[nid]!;
        if (nd.segs.length !== 1 || endDir.has(nid)) continue;
        const atStart = sg.a === nid;
        const back = Math.min(sg.len * 0.8, Math.max(8, 3 * sg.thick + 8));
        const inner = pointAlong(sg.pts, atStart ? Math.min(1, back / Math.max(sg.len, 1e-6)) : Math.max(0, 1 - back / Math.max(sg.len, 1e-6)));
        const l = Math.hypot(nd.x - inner.x, nd.y - inner.y) || 1;
        endDir.set(nid, { x: (nd.x - inner.x) / l, y: (nd.y - inner.y) / l });
      }
    }
    const endNodes = [...endDir.keys()];
    for (let i = 0; i < endNodes.length; i++) for (let j = i + 1; j < endNodes.length; j++) {
      const ia = endNodes[i]!,
        ib = endNodes[j]!;
      const na = plan.nodes[ia]!,
        nb = plan.nodes[ib]!;
      const vx = nb.x - na.x,
        vy = nb.y - na.y;
      const gap = Math.hypot(vx, vy);
      if (gap < 1 || gap > maxJump) continue;
      const ux = vx / gap,
        uy = vy / gap;
      const da = endDir.get(ia)!,
        db = endDir.get(ib)!;
      if (da.x * ux + da.y * uy < 0.7 || -(db.x * ux + db.y * uy) < 0.7) continue;
      let along = true;
      for (const f of [0.2, 0.5, 0.8]) {
        if (distToPolyline({ x: na.x + vx * f, y: na.y + vy * f }, stroke) > 1.5 * tol) {
          along = false;
          break;
        }
      }
      if (along) add(ia, ib, gap * 2.5, [na.x, na.y, nb.x, nb.y]);
    }
    // Dijkstra from SRC to SNK
    const distMap = new Map<number, number>();
    const prev = new Map<number, { from: number; edge: Edge }>();
    const open: { node: number; d: number }[] = [{ node: SRC, d: 0 }];
    distMap.set(SRC, 0);
    while (open.length) {
      let bi = 0;
      for (let i = 1; i < open.length; i++) if (open[i]!.d < open[bi]!.d) bi = i;
      const cur = open.splice(bi, 1)[0]!;
      if (cur.d > (distMap.get(cur.node) ?? Infinity)) continue;
      if (cur.node === SNK) break;
      for (const e of adj.get(cur.node) ?? []) {
        const nd = cur.d + e.cost;
        if (nd < (distMap.get(e.to) ?? Infinity)) {
          distMap.set(e.to, nd);
          prev.set(e.to, { from: cur.node, edge: e });
          open.push({ node: e.to, d: nd });
        }
      }
    }
    if (!distMap.has(SNK)) return null;
    const parts: number[][] = [];
    let node = SNK;
    while (node !== SRC) {
      const p = prev.get(node);
      if (!p) return null;
      if (p.from !== SRC && node !== SNK) parts.push(p.edge.geom);
      node = p.from;
    }
    parts.reverse();
    const out: number[] = [];
    for (const g of parts) for (let i = 0; i < g.length; i += 2) {
      const x = g[i]!,
        y = g[i + 1]!;
      const m = out.length;
      if (m === 0 || Math.hypot(x - out[m - 2]!, y - out[m - 1]!) > 0.5) out.push(x, y);
    }
    return out.length >= 4 ? simplify(out, 0.6) : null;
  }

  #segsInRect(r: Rect): number[] {
    const out: number[] = [];
    const seen = new Set<number>();
    const x0 = Math.floor(r.x / CELL),
      x1 = Math.floor((r.x + r.w) / CELL),
      y0 = Math.floor(r.y / CELL),
      y1 = Math.floor((r.y + r.h) / CELL);
    for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) {
      const arr = this.#grid.get(cy * this.#cols + cx);
      if (!arr) continue;
      for (const id of arr) if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
    return out;
  }

  /**
   * Look for parallel line work on either side of the traced path (a duct
   * bank's outer lines, a trench, a road). Returns the band midline and width.
   */
  #fitBand(path: number[], tol: number, defaultWidth: number): { midline: number[]; width: number; label: string } {
    const { k } = this.plan;
    // Parallel partners are only plausible within a plan-relative distance (duct banks, trenches,
    // curb-to-curb roads); anything farther is unrelated line work.
    const maxW = Math.min(0.035 * Math.max(this.plan.w, this.plan.h), 5 * tol) * k; // working px
    const samples = resample(path, Math.max(6, tol / 2));
    const nS = samples.length / 2;
    const leftHits: number[][] = [],
      rightHits: number[][] = [];
    let ownThick = 0;
    for (let i = 0; i < samples.length; i += 2) {
      const x = samples[i]!,
        y = samples[i + 1]!;
      const t = tangentAt(path, x, y);
      const nx = -t.y,
        ny = t.x;
      const own = this.#walk(x * k, y * k, nx, ny, 0, 6, true) + this.#walk(x * k, y * k, -nx, -ny, 0, 6, true);
      ownThick += own;
      leftHits.push(this.#walkAll(x * k, y * k, nx, ny, own / 2 + 2, maxW));
      rightHits.push(this.#walkAll(x * k, y * k, -nx, -ny, own / 2 + 2, maxW));
    }
    const thick = ownThick / nS / k;
    const dl = nearestPartner(leftHits, nS),
      dr = nearestPartner(rightHits, nS);
    const summary = (hits: number[][]) => {
      const c = new Map<number, number>();
      for (const h of hits) for (const d of h) c.set(Math.round(d / 5) * 5, (c.get(Math.round(d / 5) * 5) ?? 0) + 1);
      return [...c.entries()].sort((a, b) => a[0] - b[0]).map(([d, n]) => `${d}:${n}`).join(' ');
    };
    this.lastDebug = { ...(this.lastDebug as object), nS, thick: +thick.toFixed(1), maxW: +maxW.toFixed(0), left: summary(leftHits), right: summary(rightHits), dl, dr };
    if (dl === null && dr === null) return { midline: path, width: defaultWidth, label: 'Along line' };
    const L = (dl ?? 0) / k,
      R = (dr ?? 0) / k;
    const margin = Math.max(2, thick);
    let width: number, offset: number;
    if (dl !== null && dr !== null) {
      width = L + R + 2 * margin + thick;
      offset = (L - R) / 2;
    } else if (dl !== null) {
      width = L + 2 * margin + thick;
      offset = L / 2;
    } else {
      width = R + 2 * margin + thick;
      offset = -R / 2;
    }
    return { midline: offsetPolyline(path, offset), width, label: dl !== null && dr !== null ? 'Between lines' : 'Along pair' };
  }

  /** All ink hits (distances) along a ray, skipping through each line's thickness. */
  #walkAll(x: number, y: number, nx: number, ny: number, from: number, max: number): number[] {
    const { lineMask, cw, ch } = this.plan;
    const out: number[] = [];
    let inInk = false;
    for (let d = from; d <= max; d += 1) {
      const px = Math.floor(x + nx * d),
        py = Math.floor(y + ny * d);
      if (px < 0 || py < 0 || px >= cw || py >= ch) break;
      const ink = lineMask[py * cw + px] === 1;
      if (ink && !inInk) out.push(d);
      inInk = ink;
      if (out.length >= 4) break;
    }
    return out;
  }

  /** Walk from (x,y) (working px) along (nx,ny): returns distance to first ink hit in (from, max], or -1. If `inside`, returns distance to first background. */
  #walk(x: number, y: number, nx: number, ny: number, from: number, max: number, inside: boolean): number {
    const { lineMask, cw, ch } = this.plan;
    for (let d = from; d <= max; d += 1) {
      const px = Math.floor(x + nx * d),
        py = Math.floor(y + ny * d);
      if (px < 0 || py < 0 || px >= cw || py >= ch) return inside ? d : -1;
      const ink = lineMask[py * cw + px] === 1;
      if (inside ? !ink : ink) return d;
    }
    return inside ? max : -1;
  }

  /* ------------------------------------------------------------ region */

  lastDebug: unknown = null;

  /** Snap each side of an axis-aligned rectangle to the nearest parallel plan line. */
  #magneticRect(rect: number[], tol: number): number[] {
    let [x0, y0, x1, , , y1] = rect as [number, number, number, number, number, number, number, number];
    const snapEdge = (horizontal: boolean, coord: number, a: number, b: number): number => {
      // probe three points along the edge; take the median of consistent snaps
      const snaps: number[] = [];
      for (const f of [0.25, 0.5, 0.75]) {
        const p = horizontal ? { x: a + (b - a) * f, y: coord } : { x: coord, y: a + (b - a) * f };
        const h = this.nearest(p, tol, (sg) => {
          const dx = sg.pts[sg.pts.length - 2]! - sg.pts[0]!,
            dy = sg.pts[sg.pts.length - 1]! - sg.pts[1]!;
          const l = Math.hypot(dx, dy) || 1;
          const along = horizontal ? Math.abs(dx) / l : Math.abs(dy) / l;
          return along < 0.9 || l < tol; // must be a line parallel to this edge
        });
        if (h) snaps.push(horizontal ? h.point.y : h.point.x);
      }
      if (snaps.length < 2) return coord;
      snaps.sort((p, q) => p - q);
      const med = snaps[Math.floor(snaps.length / 2)]!;
      return snaps.every((v) => Math.abs(v - med) < tol * 0.5) ? med : coord;
    };
    y0 = snapEdge(true, y0, x0, x1);
    y1 = snapEdge(true, y1, x0, x1);
    x0 = snapEdge(false, x0, y0, y1);
    x1 = snapEdge(false, x1, y0, y1);
    return [x0, y0, x1, y0, x1, y1, x0, y1];
  }

  #region(pts: number[], bbox: Rect, closed: boolean, tol: number): Intent | null {
    const { k, cw, ch } = this.plan;
    if (closed) {
      const loop = cleanLoop(pts, tol);
      const rect = asRectangle(loop, tol * 1.2);
      if (rect) return { kind: 'region', pts: this.#magneticRect(rect, tol), label: 'Rectangle' };
    }
    const dbg: Record<string, unknown> = { closed, seeds: [] as unknown[], tries: [] as unknown[] };
    this.lastDebug = dbg;
    const strokeArea = closed ? Math.abs(polygonArea(pts)) * k * k : bbox.w * bbox.h * k * k;
    const seeds: Vec[] = [];
    if (closed) {
      // interior samples of the loop
      const c = centroid(pts);
      seeds.push(c);
      for (let gy = 0.25; gy <= 0.75; gy += 0.25) for (let gx = 0.25; gx <= 0.75; gx += 0.25) {
        const p = { x: bbox.x + bbox.w * gx, y: bbox.y + bbox.h * gy };
        if (pointInPolygon(p, pts)) seeds.push(p);
      }
    } else {
      seeds.push(centroid(pts));
      for (let i = 0; i < pts.length; i += Math.max(2, Math.floor(pts.length / 6) * 2)) seeds.push({ x: pts[i]!, y: pts[i + 1]! });
    }
    const maxArea = closed ? 4 * strokeArea + 400 * k * k : Math.max(30 * strokeArea, 5000 * k * k);
    const cap = Math.min(0.35 * cw * ch, maxArea * 1.5);
    (dbg.seeds as unknown[]).push(...seeds.map((sd) => [Math.round(sd.x), Math.round(sd.y)]));
    dbg.strokeArea = strokeArea;
    dbg.maxArea = maxArea;
    dbg.cap = cap;
    const candidates: { pts: number[]; area: number; faces: number; rect: boolean }[] = [];
    for (const r of [1, 2, 4, 7]) {
      const faces: Face[] = [];
      let total = 0;
      const tried: unknown[] = [];
      for (const sd of seeds) {
        const sx = Math.floor(sd.x * k),
          sy = Math.floor(sd.y * k);
        if (faces.some((f) => sx >= f.minX && sx <= f.maxX && sy >= f.minY && sy <= f.maxY && f.mask[(sy - f.minY) * f.bw + (sx - f.minX)])) continue;
        const key = `${r}:${sx >> 2}:${sy >> 2}`;
        let f = this.#faceCache.get(key);
        if (f === undefined) {
          f = floodFace(this.plan, sx, sy, r, cap);
          this.#faceCache.set(key, f);
        }
        tried.push(f ? f.area : `null(edt=${this.plan.edt[sy * cw + sx]})`);
        if (!f) continue;
        faces.push(f);
        total += f.area;
        if (total > maxArea) break;
      }
      (dbg.tries as unknown[]).push({ r, tried, total });
      if (!faces.length || total > maxArea) continue;
      if (closed && total < 0.25 * strokeArea) continue; // faces much smaller than the loop: the loop is the intent
      if (!closed && total < 0.05 * strokeArea) continue; // a speck beside what the scribble covers is not what it meant
      const poly = this.#unionContour(faces);
      if (poly.length < 6) continue;
      const snapped = this.#snapPolygon(poly, tol);
      const rect = asRectangle(snapped, tol);
      candidates.push({ pts: rect ?? snapped, area: Math.abs(polygonArea(rect ?? snapped)), faces: faces.length, rect: !!rect });
      // Near-convex regions with notches (symbols, leaders, hatching inside) → their hull.
      if (!rect && snapped.length / 2 > 12) {
        const h = hull(snapped);
        const ha = Math.abs(polygonArea(h));
        if (h.length >= 6 && Math.abs(polygonArea(snapped)) >= 0.7 * ha) {
          const hs = simplify([...h, h[0]!, h[1]!], tol * 0.25);
          const hp = hs.slice(0, hs.length - 2);
          const hr = asRectangle(hp, tol);
          candidates.push({ pts: hr ?? hp, area: ha, faces: faces.length, rect: !!hr });
        }
      }
    }
    if (!candidates.length) return null;
    // Prefer the cleanest candidate: fewest vertices among those not much smaller than the largest.
    const maxA = Math.max(...candidates.map((c) => c.area));
    const ok = candidates.filter((c) => c.area >= 0.8 * maxA);
    ok.sort((a, b) => a.pts.length - b.pts.length);
    const best = ok[0]!;
    if (closed && !best.rect) {
      // The user's own loop, cleaned, is preferred when the enclosed faces are fragmented.
      const loop = this.#snapPolygon(cleanLoop(pts, tol), tol);
      const la = Math.abs(polygonArea(loop));
      if (best.pts.length / 2 > 2 * (loop.length / 2) + 6 && best.area > 0.6 * la && best.area < 1.4 * la) return { kind: 'region', pts: loop, label: 'Area' };
    }
    return { kind: 'region', pts: best.pts, label: best.rect ? 'Rectangle' : best.faces > 1 ? 'Enclosed areas' : 'Enclosed area' };
  }

  #unionContour(faces: Face[]): number[] {
    const pad = Math.ceil(faces[0]!.r + 3);
    const minX = Math.min(...faces.map((f) => f.minX)) - pad,
      minY = Math.min(...faces.map((f) => f.minY)) - pad,
      maxX = Math.max(...faces.map((f) => f.maxX)) + pad,
      maxY = Math.max(...faces.map((f) => f.maxY)) + pad;
    const bw = maxX - minX + 1,
      bh = maxY - minY + 1;
    const mask = new Uint8Array(bw * bh);
    for (const f of faces) for (let y = 0; y < f.bh; y++) for (let x = 0; x < f.bw; x++) if (f.mask[y * f.bw + x]) mask[(f.minY - minY + y) * bw + (f.minX - minX + x)] = 1;
    // Morphological closing so faces separated by a line (2–3 px) merge into one blob.
    const closed = closing(mask, bw, bh, pad);
    // Largest blob only.
    const { labels, comps } = labelComponents({ w: bw, h: bh, data: closed }, false);
    if (!comps.length) return [];
    const big = comps.reduce((a, c) => (c.count > a.count ? c : a));
    const blob = new Uint8Array(bw * bh);
    for (let i = 0; i < blob.length; i++) blob[i] = labels[i] === big.id ? 1 : 0;
    const merged: Face = { r: faces[0]!.r, area: big.count, minX, minY, maxX, maxY, mask: blob, bw, bh };
    // faceContour expands by r; the closing added `pad`, so pull back in by pad.
    const poly = faceContour(merged, this.plan, 1.2);
    return expandPolygon(poly, -pad / this.plan.k);
  }

  /** Pull polygon vertices onto nearby junctions/lines and drop micro-detours. */
  #snapPolygon(poly: number[], tol: number): number[] {
    const out: number[] = [];
    for (let i = 0; i < poly.length; i += 2) {
      const p = { x: poly[i]!, y: poly[i + 1]! };
      const s = this.snapPoint(p, tol * 0.8);
      out.push(s.p.x, s.p.y);
    }
    const simp = simplify(out, Math.max(1.2, tol * 0.15));
    return simp.length >= 6 ? simp : out;
  }
}

/** Intent without a plan analysis (still loading): the cleaned stroke itself. */
export function fallbackIntent(stroke: number[], tol: number, defaultWidth: number): Intent {
  const pts = dedupe(stroke, 1);
  if (pts.length < 4) return { kind: 'none' };
  const L = polylineLength(pts);
  if (L < 4 * tol) return { kind: 'none' };
  const start = { x: pts[0]!, y: pts[1]! },
    end = { x: pts[pts.length - 2]!, y: pts[pts.length - 1]! };
  const loopArea = Math.abs(polygonArea(pts));
  const closed = dist(start, end) <= Math.max(3 * tol, 0.18 * L) && L >= 10 * tol && loopArea >= 8 * tol * tol && loopArea >= 0.03 * L * L;
  if (closed) {
    const loop = cleanLoop(pts, tol);
    return { kind: 'free-poly', pts: asRectangle(loop, tol * 1.2) ?? loop, label: 'Area' };
  }
  return { kind: 'free-stroke', pts: cleanStroke(pts, tol), width: defaultWidth, label: 'Free line' };
}

/* ------------------------------------------------------------ helpers */

/**
 * A stroke whose end crosses back over its beginning, round a loop that is most of it: the loop
 * encloses real area (`encloses`), so it is a loop run on past its start, not a scribble (a
 * back-and-forth scribble crosses itself too, but encloses next to nothing for its length).
 */
function overshotLoop(pts: number[], L: number, encloses: (area: number, len: number) => boolean): boolean {
  // a couple of hundred points are plenty to find the crossing
  const stride = Math.max(1, Math.ceil(pts.length / 2 / 240));
  const P: Vec[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2 * stride) P.push({ x: pts[i]!, y: pts[i + 1]! });
  if (pts.length >= 4 && (pts.length / 2 - 1) % stride) P.push({ x: pts[pts.length - 2]!, y: pts[pts.length - 1]! });
  const at: number[] = [0];
  for (let i = 1; i < P.length; i++) at.push(at[i - 1]! + dist(P[i - 1]!, P[i]!));
  const n = P.length;
  for (let i = n - 2; i > 0 && at[i + 1]! > 0.6 * L; i--) {
    for (let j = 0; j + 1 < i && at[j]! < 0.4 * L; j++) {
      if (!segmentsIntersect(P[i]!, P[i + 1]!, P[j]!, P[j + 1]!)) continue;
      const loop = P.slice(j + 1, i + 1);
      const len = at[i]! - at[j + 1]!;
      if (loop.length < 3 || len < 0.6 * L) return false;
      return encloses(Math.abs(polygonArea(loop.flatMap((q) => [q.x, q.y]))), len);
    }
  }
  return false;
}

/**
 * Is the stroke a closed loop (fill what it encloses) or a scribble (fill what
 * it covers)? Anything else is an open line.
 */
export function strokeForm(pts: number[], tol: number): { closed: boolean; scribble: boolean } {
  if (pts.length < 4) return { closed: false, scribble: false };
  const L = polylineLength(pts);
  const bbox = polyBounds(pts);
  const start = { x: pts[0]!, y: pts[1]! };
  const end = { x: pts[pts.length - 2]!, y: pts[pts.length - 1]! };
  const closure = dist(start, end);
  const diag = Math.hypot(bbox.w, bbox.h);
  const loopArea = Math.abs(polygonArea(pts));
  // A loop encloses real area for its length (circle: 0.08·L², square: 0.0625·L²);
  // a back-and-forth scribble that happens to end near its start does not.
  const encloses = (area: number, len: number): boolean => len >= 10 * tol && area >= 8 * tol * tol && area >= 0.03 * len * len;
  // ...or it ran on past its start (round once and on along its first side, as a finger often
  // does): it crosses its own beginning, and the loop between the crossings is most of it
  const closed = (closure <= Math.max(3 * tol, 0.18 * L) && encloses(loopArea, L)) || overshotLoop(pts, L, encloses);
  // A scribble fills an area: it spreads sideways as well as along. A finger running back and
  // forth along one line (drawing a band and cutting it back) covers a long thin box and is no scribble.
  const spread = Math.min(bbox.w, bbox.h) >= Math.max(4 * tol, 0.25 * Math.max(bbox.w, bbox.h));
  const scribble = !closed && L >= 10 * tol && diag < 0.5 * L && spread;
  return { closed, scribble };
}

/**
 * Distances (working px) at which most samples see a parallel line (ink within
 * ±3 px in at least 65% of samples), merged when closer than 4 px.
 */
function consistentDistances(hits: number[][], nS: number, minSupport = 0.65): number[] {
  const uniq = [...new Set(hits.flat().map((d) => Math.round(d)))].sort((a, b) => a - b);
  const out: number[] = [];
  for (const D of uniq) {
    let support = 0;
    for (const h of hits) if (h.some((d) => d >= D - 3 && d <= D + 3)) support++;
    if (support / Math.max(1, nS) < minSupport) continue;
    if (out.length && D - out[out.length - 1]! < 4) continue;
    out.push(D);
  }
  return out;
}
/** Nearest consistent parallel line, if any. */
function nearestPartner(hits: number[][], nS: number): number | null {
  const all = consistentDistances(hits, nS);
  return all.length ? all[0]! : null;
}

/** Morphological closing (dilate then erode) with a square kernel of radius r. */
function closing(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const dil = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!mask[y * w + x]) continue;
    for (let dy = -r; dy <= r; dy++) {
      const yy = y + dy;
      if (yy < 0 || yy >= h) continue;
      const x0 = Math.max(0, x - r),
        x1 = Math.min(w - 1, x + r);
      dil.fill(1, yy * w + x0, yy * w + x1 + 1);
    }
  }
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let all = 1;
    for (let dy = -r; dy <= r && all; dy++) {
      const yy = y + dy;
      if (yy < 0 || yy >= h) {
        all = 0;
        break;
      }
      for (let dx = -r; dx <= r; dx++) {
        const xx = x + dx;
        if (xx < 0 || xx >= w || !dil[yy * w + xx]) {
          all = 0;
          break;
        }
      }
    }
    out[y * w + x] = all;
  }
  return out;
}

/**
 * If a polygon is essentially an axis-aligned rectangle (4 dominant corners,
 * edges within tol of the bounding box), return the exact rectangle.
 */
function asRectangle(poly: number[], tol: number): number[] | null {
  const n = poly.length / 2;
  if (n < 4) return null;
  const b = polyBounds(poly);
  if (b.w < 3 * tol || b.h < 3 * tol) return null;
  const area = Math.abs(polygonArea(poly));
  if (area < 0.9 * b.w * b.h) return null;
  // every vertex must lie near the bbox boundary
  for (let i = 0; i < poly.length; i += 2) {
    const x = poly[i]!,
      y = poly[i + 1]!;
    const dx = Math.min(Math.abs(x - b.x), Math.abs(x - (b.x + b.w)));
    const dy = Math.min(Math.abs(y - b.y), Math.abs(y - (b.y + b.h)));
    if (Math.min(dx, dy) > tol * 0.8) return null;
  }
  return [b.x, b.y, b.x + b.w, b.y, b.x + b.w, b.y + b.h, b.x, b.y + b.h];
}

function polygonArea(pts: number[]): number {
  let a = 0;
  const n = pts.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += pts[2 * i]! * pts[2 * j + 1]! - pts[2 * j]! * pts[2 * i + 1]!;
  }
  return a / 2;
}
function centroid(pts: number[]): Vec {
  let x = 0,
    y = 0;
  const n = pts.length / 2;
  for (let i = 0; i < pts.length; i += 2) {
    x += pts[i]!;
    y += pts[i + 1]!;
  }
  return { x: x / n, y: y / n };
}
function pointInPolygon(p: Vec, pts: number[]): boolean {
  const n = pts.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = pts[2 * i]!,
      yi = pts[2 * i + 1]!,
      xj = pts[2 * j]!,
      yj = pts[2 * j + 1]!;
    if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
function distToPolyline(p: Vec, pts: number[]): number {
  let best = Infinity;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const d = nearestOnSegment(p, { x: pts[i]!, y: pts[i + 1]! }, { x: pts[i + 2]!, y: pts[i + 3]! }).d;
    if (d < best) best = d;
  }
  return best;
}
function reversePts(pts: number[]): number[] {
  const out: number[] = [];
  for (let i = pts.length - 2; i >= 0; i -= 2) out.push(pts[i]!, pts[i + 1]!);
  return out;
}
/** Sub-polyline between fractional vertex positions t0 <= t1 (t = index + fraction). */
function slicePolyline(pts: number[], t0: number, t1: number): number[] {
  const at = (t: number): Vec => {
    const i = Math.min(Math.floor(t), pts.length / 2 - 1);
    const f = t - i;
    const x0 = pts[2 * i]!,
      y0 = pts[2 * i + 1]!;
    if (f <= 1e-9 || 2 * i + 3 >= pts.length) return { x: x0, y: y0 };
    return { x: x0 + (pts[2 * i + 2]! - x0) * f, y: y0 + (pts[2 * i + 3]! - y0) * f };
  };
  const out: number[] = [];
  const a = at(t0);
  out.push(a.x, a.y);
  for (let i = Math.floor(t0) + 1; i <= Math.floor(t1) && i < pts.length / 2; i++) {
    if (i > t1) break;
    out.push(pts[2 * i]!, pts[2 * i + 1]!);
  }
  const b = at(t1);
  out.push(b.x, b.y);
  return out;
}
function resample(pts: number[], step: number): number[] {
  const out: number[] = [];
  const total = polylineLength(pts);
  const n = Math.max(2, Math.floor(total / step));
  for (let i = 0; i <= n; i++) {
    const p = pointAt(pts, (i / n) * total);
    out.push(p.x, p.y);
  }
  return out;
}
function pointAt(pts: number[], s: number): Vec {
  let acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const ax = pts[i]!,
      ay = pts[i + 1]!,
      bx = pts[i + 2]!,
      by = pts[i + 3]!;
    const l = Math.hypot(bx - ax, by - ay);
    if (acc + l >= s || i + 4 >= pts.length) {
      const t = l === 0 ? 0 : Math.min(1, Math.max(0, (s - acc) / l));
      return { x: ax + (bx - ax) * t, y: ay + (by - ay) * t };
    }
    acc += l;
  }
  return { x: pts[0]!, y: pts[1]! };
}
function tangentAt(pts: number[], x: number, y: number): Vec {
  let bi = 0,
    bd = Infinity;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const d = nearestOnSegment({ x, y }, { x: pts[i]!, y: pts[i + 1]! }, { x: pts[i + 2]!, y: pts[i + 3]! }).d;
    if (d < bd) {
      bd = d;
      bi = i;
    }
  }
  const dx = pts[bi + 2]! - pts[bi]!,
    dy = pts[bi + 3]! - pts[bi + 1]!;
  const l = Math.hypot(dx, dy) || 1;
  return { x: dx / l, y: dy / l };
}
function offsetPolyline(pts: number[], d: number): number[] {
  if (Math.abs(d) < 1e-6) return pts;
  const n = pts.length / 2;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const p = Math.max(0, i - 1),
      q = Math.min(n - 1, i + 1);
    const dx = pts[2 * q]! - pts[2 * p]!,
      dy = pts[2 * q + 1]! - pts[2 * p + 1]!;
    const l = Math.hypot(dx, dy) || 1;
    out.push(pts[2 * i]! + (-dy / l) * d, pts[2 * i + 1]! + (dx / l) * d);
  }
  return out;
}
/**
 * Remove lateral hops between parallel pieces of line work: resample, moving
 * average over ~2.4·tol of arc length (endpoints fixed), then simplify.
 */
function smoothPath(path: number[], tol: number): number[] {
  const step = Math.max(2, tol / 3);
  const rs = resample(path, step);
  const n = rs.length / 2;
  if (n < 5) return path;
  const radius = Math.max(1, Math.round((1.2 * tol) / step));
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    if (i === 0 || i === n - 1) {
      out.push(rs[2 * i]!, rs[2 * i + 1]!);
      continue;
    }
    const r = Math.min(radius, i, n - 1 - i); // shrink the window near the ends so they stay anchored
    let sx = 0,
      sy = 0,
      c = 0;
    for (let j = i - r; j <= i + r; j++) {
      sx += rs[2 * j]!;
      sy += rs[2 * j + 1]!;
      c++;
    }
    out.push(sx / c, sy / c);
  }
  return simplify(out, tol * 0.2);
}

function cleanStroke(pts: number[], tol: number): number[] {
  const sm = chaikin(simplify(pts, tol * 0.35), 1, false);
  return simplify(sm, tol * 0.15);
}
function cleanLoop(pts: number[], tol: number): number[] {
  const sm = chaikin(simplify(pts, tol * 0.35), 1, true);
  const s = simplify([...sm, sm[0]!, sm[1]!], tol * 0.15);
  return s.slice(0, s.length - 2);
}
/** Convex hull (monotone chain) of a flat point list. */
function hull(pts: number[]): number[] {
  const p: Vec[] = [];
  for (let i = 0; i < pts.length; i += 2) p.push({ x: pts[i]!, y: pts[i + 1]! });
  p.sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Vec, a: Vec, b: Vec) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Vec[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: Vec[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, q) <= 0) upper.pop();
    upper.push(q);
  }
  const h = lower.slice(0, -1).concat(upper.slice(0, -1));
  return h.flatMap((q) => [q.x, q.y]);
}
